// Direction B "Ink Header" reschedule card. Per spec §3.5 + §7.11: rendered
// when reschedule_appointment reaches `output-available`. Second of 3
// moment-of-truth cards (Q1+Q5 rulings).
//
// Per design handoff: title is the new datetime ("Moved to Monday, 4:00 PM"),
// rows show From → To → Where. Eyebrow uses the success palette (green check
// + "Tour rescheduled"). Foot: "Updated invite sent to {email}".
//
// Per spec §7.19: both datetimes render in vendor_timezone, NOT the source
// ISO offset or the visitor's browser TZ. formatDatetime is the single source
// of truth for date display.
//
// `old_datetime` is sourced from part.output. The shared reschedule_appointment
// schema does not carry it — chat-only enrichment in
// chat_pipeline._enrich_chat_card_output injects `old_datetime` into the tool
// result from envelope.booking_data so this card can render it without
// polluting the email/SMS tool schema.

import { useVendorInfo } from '../../context/VendorInfoContext'
import { parseToolOutput } from '../../lib/parse-tool-output'
import { formatDatetime } from '../../lib/format-datetime'
import { bookingStatusLabel, whereValue } from '../../lib/booking-labels'
import { ActionCard } from './ActionCard'

export interface RescheduleCardProps {
  input: Record<string, unknown>
  output: unknown
}

export function RescheduleCard({ input, output }: RescheduleCardProps) {
  const vendor = useVendorInfo()
  const parsed = parseToolOutput(output) ?? {}
  // RCA Change 1: render off the RESULT. reschedule_appointment returns
  // {status:'rescheduled'} on success, {status:'error'} on failure. Allowlist the
  // success status so malformed/empty output also renders nothing.
  if (parsed.status !== 'rescheduled') return null
  const oldDt = (parsed.old_datetime as string) || ''
  const newDt =
    (parsed.appointment_datetime as string) ||
    (input?.new_datetime_iso as string) ||
    ''
  const email = (parsed.attendee_email as string) || ''
  const oldF = formatDatetime(oldDt, vendor.vendor_timezone)
  const newF = formatDatetime(newDt, vendor.vendor_timezone)

  const rows = [] as { label: string; text: React.ReactNode }[]
  if (oldF) rows.push({ label: 'From', text: oldF })
  if (newF) rows.push({ label: 'To', text: newF })
  rows.push({ label: 'Where', text: whereValue(parsed, vendor.business_name) })

  const label = bookingStatusLabel(vendor.booking_noun, 'rescheduled')

  // Title: "Moved to {newF}" if we have a new datetime; fallback to the vendor
  // booking-noun label ("Tour rescheduled" for legacy) so the card still reads
  // cleanly when both are missing.
  const title = newF ? `Moved to ${newF}` : label

  return (
    <ActionCard
      testId="reschedule-card"
      status="success"
      eyebrow={label}
      title={title}
      rows={rows}
      foot={
        email ? (
          <>
            Updated invite sent to <b>{email}</b>
          </>
        ) : null
      }
    />
  )
}
